import React, { useEffect, useMemo, useRef, useState } from "react";

export function LanguageSwitcher({
  className = "",
  variant = "desktop", // "desktop" | "mobile"
  languages = [],
  current, // ex: "pt-BR"
  onChange, // (lang) => void  | opcional
  showLabelsOnMobile = true, // se quiser mostrar texto junto no dropdown
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = useMemo(
    () => languages.find((l) => l.code === current) || languages[0],
    [languages, current]
  );

  const available = useMemo(
    () => languages.filter((l) => l.code !== currentLang?.code),
    [languages, currentLang]
  );

  const orderedLanguages = useMemo(
    () => [currentLang, ...available].filter(Boolean),
    [currentLang, available]
  );

  function handleSelect(lang) {
    setOpen(false);

    if (typeof onChange === "function") {
      onChange(lang);
      return;
    }

    // fallback: se tiver url, navega
    if (lang?.href) window.location.href = lang.href;
  }

  // Fecha ao clicar fora / ESC
  useEffect(() => {
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  if (!languages?.length || !currentLang) return null;

  // ---------------------------
  // DESKTOP: lista de bandeiras
  // ---------------------------
  if (variant === "desktop") {
    return (
      <ul
        className={`flags-lang d-flex align-items-center justify-content-end ${className}`}
      >
        {orderedLanguages.map((lang) => {
          const isCurrent = lang.code === currentLang.code;

          return (
            <li key={lang.code} className={isCurrent ? "is-current" : ""}>
              <a
                href={lang.href || "#"}
                onClick={(e) => {
                  if (!lang.href || isCurrent) e.preventDefault();
                  if (!isCurrent) handleSelect(lang);
                }}
                aria-current={isCurrent ? "true" : "false"}
                title={lang.label}
              >
                <img src={lang.icon} width="25" alt={lang.label} />
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  // ---------------------------
  // MOBILE: bandeira atual + dropdown
  // ---------------------------
  return (
    <div ref={ref} className={`lang-switcher ${className}`}>
      <button
        type="button"
        className="lang-switcher__current"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
        aria-label={`Seletor de idioma - Idioma atual: ${currentLang.label}`}
      >
        <img src={currentLang.icon} width="35" alt={currentLang.label} />
      </button>

      <ul className={`${open ? "open" : ""} lang-switcher__menu`} role="menu">
        {orderedLanguages.map((lang) => {
          const isCurrent = lang.code === currentLang.code;

          return (
          <li
            key={lang.code}
            role="none"
            className={isCurrent ? "is-current" : ""}
          >
            <button
              type="button"
              role="menuitem"
              className="lang-switcher__item"
              onClick={() => {
                if (!isCurrent) handleSelect(lang);
              }}
              aria-current={isCurrent ? "true" : "false"}
            >
              <img src={lang.icon} width="22" alt={lang.label} />
              {showLabelsOnMobile && (
                <span className="lang-switcher__label">{lang.label}</span>
              )}
            </button>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
